import type { AppContext } from "../../app/app";
import type { Dimension } from "../../data/model";
import { el, pct } from "../dom";

// Per-dimension ranking by relative share. There is deliberately no aggregate
// or cross-dimension score: each dimension is ranked on its own normalised
// ratings.
let selectedDimension: string | null = null;

export async function renderRankings(ctx: AppContext): Promise<HTMLElement> {
  const [dimensions, people] = await Promise.all([
    ctx.repo.listDimensions(),
    ctx.repo.listPeople(),
  ]);
  const nameOf = new Map(people.map((p) => [p.id, p.display_name]));

  const root = el("section", {});
  root.append(
    el("span", { class: "label", text: "Rankings" }),
    el("h1", { text: "Per-dimension, in your eyes" }),
    el("p", {
      class: "lede",
      text:
        "Each quality is ranked only by your relative allocation. No global " +
        "score, no average across qualities.",
    }),
  );

  if (dimensions.length === 0) {
    root.append(el("div", { class: "empty", text: "No dimensions to rank." }));
    return root;
  }

  if (!selectedDimension || !dimensions.some((d) => d.id === selectedDimension)) {
    selectedDimension = dimensions[0]!.id;
  }

  const tabs = el("div", { class: "dim-tabs" });
  for (const dim of dimensions) {
    tabs.append(
      el("button", {
        class: `dim-tab${dim.id === selectedDimension ? " is-active" : ""}`,
        text: dim.name,
        on: {
          click: () => {
            selectedDimension = dim.id;
            ctx.refresh();
          },
        },
      }),
    );
  }
  root.append(tabs);

  const dim = dimensions.find((d) => d.id === selectedDimension)!;
  root.append(await rankingTable(ctx, dim, nameOf));
  return root;
}

async function rankingTable(
  ctx: AppContext,
  dim: Dimension,
  nameOf: Map<string, string>,
): Promise<HTMLElement> {
  const entries = await ctx.lens.rankByDimension(ctx.viewer, dim.id);

  const wrap = el("div", { class: "ranking" });
  wrap.append(
    el(
      "div",
      { class: "ranking__caption" },
      el("span", { class: "label", text: dim.name }),
      el("span", {
        class: "label num",
        text: `${entries.length} rated`,
      }),
    ),
  );

  if (entries.length === 0) {
    wrap.append(el("div", { class: "empty", text: "Nobody rated on this quality yet." }));
    return wrap;
  }

  const table = el("div", { class: "rank-table" });
  entries.forEach((entry, i) => {
    const bar = el("div", { class: "rank-bar__fill" });
    bar.style.width = pct(entry.share);
    table.append(
      el(
        "div",
        { class: "rank-row" },
        el("span", { class: "rank-row__pos num", text: String(i + 1) }),
        el(
          "div",
          { class: "rank-row__body" },
          el(
            "div",
            { class: "rank-row__line" },
            el("span", {
              class: "rank-row__name",
              text: nameOf.get(entry.person_id) ?? "(unknown)",
            }),
            el(
              "span",
              { class: "rank-row__share num" },
              entry.band
                ? el("span", {
                    class: "rank-row__band",
                    text: `indicative ${entry.band.low.toFixed(1)}–${entry.band.high.toFixed(1)} · `,
                  })
                : null,
              document.createTextNode(pct(entry.share)),
            ),
          ),
          el("div", { class: "rank-bar" }, bar),
          entry.rationale
            ? el("p", { class: "rank-row__why", text: entry.rationale })
            : null,
        ),
      ),
    );
  });
  wrap.append(table);
  return wrap;
}
